import uniq from 'lodash/uniq'

import { Control } from '@/gen/inventory/v1/control_service_pb'

import type { ControlRow } from '@/pages/control/ag-grid-control/types'
import {
  type GroupHeaderExtra,
  groupControlsByKey,
} from '@/pages/control/ag-grid-control/utils/group-controls-by-key'

/**
 * Groups controls by category and creates group header rows with filtering metadata.
 *
 * This function:
 * 1. Sorts controls by category name, then by title within each category
 * 2. Groups controls by category
 * 3. Creates category header rows with metrics and filter values
 * 4. Adds child control rows only for expanded categories
 *
 * @param controls - Array of controls to group (caller should pre-filter if needed)
 * @param expandedGroups - Set of category names that are expanded
 * @returns Array of ControlRow objects containing both group headers and control rows
 */
export function groupControlsByCategory(
  controls: Control[],
  expandedGroups: Set<string>,
): ControlRow[] {
  const getGroupKey = (c: Control) => c.categoryName ?? ''

  return groupControlsByKey(controls, expandedGroups, {
    getGroupKey,
    buildHeaderExtra(_groupKey, groupControls): GroupHeaderExtra {
      const categorySmartQuestionStatuses = uniq(
        groupControls
          .map((c) => c.smartQuestion?.status)
          .filter((s): s is number => s !== undefined && s !== null),
      )
      return {
        categoryFilterValues: {
          smartQuestionStatuses: categorySmartQuestionStatuses,
        },
      }
    },
  })
}
